(() => {
  'use strict';

  const node = document.getElementById('dryfloor-cart-config');
  if (!node) return;

  let config;
  try { config = JSON.parse(node.textContent); } catch (error) { console.error('DryFloor cart config could not be parsed.', error); return; }

  const product = config.product || {};
  const variants = Array.isArray(product.variants) ? product.variants : [];
  const options = Array.isArray(product.options) ? product.options : [];
  const cartItems = Array.isArray(config.cart?.items) ? config.cart.items : [];

  // Show the same tier savings as the product builder (Shopify must apply checkout discounts).
  const setSavings = { 1: 0, 2: 999, 3: 2498, 4: 3997 };
  const dryfloorCartItems = cartItems.filter((item) => Number(item.product_id) === Number(product.id));
  const dryfloorMatCount = dryfloorCartItems.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
  const dryfloorSinglesTotal = dryfloorCartItems.reduce((sum, item) => sum + (Number(item.original_line_price) || Number(item.final_line_price) || 0), 0);
  const setTotal = (singleTotal, count) => Math.max(0, singleTotal - (setSavings[count] || 0));

  const normaliseOption = (value) => String(value ?? '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/grey/g, 'gray')
    .replace(/[×✕]/g, 'x')
    .replace(/centimet(?:re|er)s?/g, 'cm')
    .replace(/[^a-z0-9]+/g, '');

  const dimensionKey = (value) => {
    const numbers = String(value ?? '').match(/\d+(?:\.\d+)?/g);
    return numbers && numbers.length >= 2 ? `${Number(numbers[0])}x${Number(numbers[1])}` : '';
  };

  const sizeKey = (value) => {
    const n = normaliseOption(value);
    const d = dimensionKey(value);
    const compact = normaliseOption(config.optionValues?.compact);
    const large = normaliseOption(config.optionValues?.large);
    if ((compact && n === compact) || n === 'compact' || n === 'small' || d === '40x60') return 'compact';
    if ((large && n === large) || n === 'large' || d === '50x80') return 'large';
    return '';
  };

  const colourKey = (value) => {
    const n = normaliseOption(value);
    const configured = config.optionValues || {};
    if (n && n === normaliseOption(configured.charcoal)) return 'charcoal';
    if (n && n === normaliseOption(configured.lightGrey)) return 'lightGrey';
    if (n && n === normaliseOption(configured.lightBlue)) return 'lightBlue';
    if (n && n === normaliseOption(configured.forestGreen)) return 'forestGreen';
    if (['charcoal', 'charcoalgray', 'darkcharcoal', 'darkgray'].includes(n)) return 'charcoal';
    if (['lightgray', 'gray', 'palegray'].includes(n)) return 'lightGrey';
    if (['lightblue', 'blue', 'paleblue'].includes(n)) return 'lightBlue';
    if (['forestgreen', 'green', 'darkgreen'].includes(n)) return 'forestGreen';
    return '';
  };

  const packKey = (value) => {
    const n = normaliseOption(value);
    if (n && n === normaliseOption(config.optionValues?.singlePack)) return '1';
    if (n && n === normaliseOption(config.optionValues?.doublePack)) return '2';
    if (n && n === normaliseOption(config.optionValues?.triplePack)) return '3';
    if (['1pack', 'pack1', 'single', 'singlepack', '1'].includes(n)) return '1';
    if (['2pack', 'pack2', 'double', 'doublepack', '2'].includes(n)) return '2';
    if (['3pack', 'pack3', 'triple', 'triplepack', '3'].includes(n)) return '3';
    return '';
  };

  const optionValuesAt = (index) => {
    const values = [];
    variants.forEach((variant) => {
      const value = variant.options?.[index];
      if (value != null && !values.includes(value)) values.push(value);
    });
    return values;
  };

  const scoreIndex = (index, type) => optionValuesAt(index).reduce((score, value) => {
    if (type === 'size') return score + (sizeKey(value) ? 1 : 0);
    if (type === 'colour') return score + (colourKey(value) ? 1 : 0);
    return score + (packKey(value) ? 1 : 0);
  }, 0);

  let sizeIndex = options.findIndex((name) => /size|dimension/i.test(String(name)));
  let colourIndex = options.findIndex((name) => /colou?r|shade|finish/i.test(String(name)));
  let packIndex = options.findIndex((name) => /pack|bundle|quantity|qty/i.test(String(name)));
  const optionCount = Math.max(options.length, ...variants.map((variant) => variant.options?.length || 0), 0);
  if (sizeIndex < 0) {
    let best = -1, bestScore = 0;
    for (let i = 0; i < optionCount; i += 1) {
      const score = scoreIndex(i, 'size');
      if (score > bestScore) { best = i; bestScore = score; }
    }
    if (bestScore >= 2) sizeIndex = best;
  }
  if (colourIndex < 0) {
    let best = -1, bestScore = 0;
    for (let i = 0; i < optionCount; i += 1) {
      if (i === sizeIndex) continue;
      const score = scoreIndex(i, 'colour');
      if (score > bestScore) { best = i; bestScore = score; }
    }
    if (bestScore >= 2) colourIndex = best;
  }
  if (packIndex < 0) {
    let best = -1, bestScore = 0;
    for (let i = 0; i < optionCount; i += 1) {
      if (i === sizeIndex || i === colourIndex) continue;
      const score = scoreIndex(i, 'pack');
      if (score > bestScore) { best = i; bestScore = score; }
    }
    if (bestScore >= 2) packIndex = best;
  }

  const toast = document.getElementById('dryfloor-cart-toast');
  const dialog = document.getElementById('dryfloor-cart-edit');
  let editingKey = null;
  let editingOriginal = null;
  let editingPack = config.optionValues?.singlePack || '1-Pack';
  let editBusy = false;
  let toastTimer = null;

  const money = (cents) => {
    const currency = config.cart?.currency || 'GBP';
    try { return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', { style: 'currency', currency }).format((Number(cents) || 0) / 100); }
    catch (_) { return '£' + ((Number(cents) || 0) / 100).toFixed(2); }
  };

  const notify = (message) => {
    if (!toast) return;
    toast.textContent = message;
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => { toast.textContent = ''; }, 4500);
  };

  const valueKey = (value) => colourKey(value) || 'charcoal';
  const imageForColour = (value) => config.images?.[valueKey(value)] || config.images?.charcoal || '';

  const configuredSizeValue = (key) => key === 'compact' ? config.optionValues.compact : config.optionValues.large;
  const configuredColourValue = (key) => ({
    charcoal: config.optionValues.charcoal,
    lightGrey: config.optionValues.lightGrey,
    lightBlue: config.optionValues.lightBlue,
    forestGreen: config.optionValues.forestGreen
  })[key] || config.optionValues.charcoal;

  const findVariant = (size, colour, pack) => {
    if (sizeIndex < 0 || colourIndex < 0) return null;
    const wantedSize = sizeKey(size);
    const wantedColour = colourKey(colour);
    const resolvedPack = pack || config.optionValues?.singlePack || '1-Pack';
    const wantedPack = packIndex >= 0 ? packKey(resolvedPack) : '';
    if (!wantedSize || !wantedColour || (packIndex >= 0 && !wantedPack)) return null;
    return variants.find((variant) => {
      const values = Array.isArray(variant.options) ? variant.options : [];
      if (sizeKey(values[sizeIndex]) !== wantedSize || colourKey(values[colourIndex]) !== wantedColour) return false;
      return packIndex < 0 || packKey(values[packIndex]) === wantedPack;
    }) || null;
  };

  const postJSON = async (url, body) => {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.description || data.message || 'We could not update your basket.');
    return data;
  };

  const getCart = async () => {
    const response = await fetch(`${config.routes.cart}.js`, {
      method: 'GET',
      headers: { 'Accept': 'application/json' },
      cache: 'no-store'
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.description || data.message || 'We could not refresh your basket.');
    return data;
  };

  const cloneProperties = (properties) => {
    if (!properties || typeof properties !== 'object') return {};
    return Object.fromEntries(Object.entries(properties).map(([key, value]) => [key, value]));
  };

  const propertySignature = (properties) => Object.entries(cloneProperties(properties))
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => `${key}:${String(value)}`)
    .join('|');

  const sameProperties = (a, b) => propertySignature(a) === propertySignature(b);

  const lineIdentity = (item) => ({
    key: item?.key || '',
    product_id: Number(item?.product_id) || 0,
    variant_id: Number(item?.variant_id) || 0,
    quantity: Number(item?.quantity) || 1,
    properties: cloneProperties(item?.properties),
    bundleId: item?.properties?._dryfloor_bundle_id || '',
    slot: item?.properties?._dryfloor_slot || '',
    sellingPlan: item?.selling_plan_allocation?.selling_plan?.id || null
  });

  const resolveLiveLine = (liveCart, original) => {
    const items = Array.isArray(liveCart?.items) ? liveCart.items : [];
    if (!original) return null;

    // DryFloor lines added by this theme have a private bundle + slot identity.
    // This survives discount recalculation even when Shopify changes the line key.
    if (original.bundleId && original.slot) {
      const exactSlot = items.filter((item) =>
        Number(item.product_id) === original.product_id &&
        item.properties?._dryfloor_bundle_id === original.bundleId &&
        item.properties?._dryfloor_slot === original.slot
      );
      if (exactSlot.length === 1) return exactSlot[0];
    }

    const sameKey = items.find((item) => item.key === original.key);
    if (sameKey) return sameKey;

    const exact = items.filter((item) =>
      Number(item.product_id) === original.product_id &&
      Number(item.variant_id) === original.variant_id &&
      sameProperties(item.properties, original.properties)
    );
    return exact.length === 1 ? exact[0] : null;
  };

  const addLinePayload = (variantId, sourceLine) => {
    const item = {
      id: Number(variantId),
      quantity: Number(sourceLine?.quantity) || 1,
      properties: cloneProperties(sourceLine?.properties)
    };
    const sellingPlan = sourceLine?.selling_plan_allocation?.selling_plan?.id || sourceLine?.sellingPlan || null;
    if (sellingPlan) item.selling_plan = sellingPlan;
    return item;
  };

  const reloadCart = () => { window.location.assign(config.routes.cart); };

  document.querySelectorAll('[data-df-remove]').forEach((button) => {
    button.addEventListener('click', async () => {
      const key = button.dataset.lineKey;
      button.disabled = true;
      try {
        await postJSON(config.routes.change, { id: key, quantity: 0 });
        reloadCart();
      } catch (error) {
        button.disabled = false;
        notify(error.message);
      }
    });
  });

  if (dialog) {
    const close = dialog.querySelector('.close');
    const sizeSelect = dialog.querySelector('[data-df-edit-size]');
    const colourSelect = dialog.querySelector('[data-df-edit-colour]');
    const image = dialog.querySelector('[data-df-edit-image]');
    const thumb = dialog.querySelector('[data-df-edit-thumb]');
    const save = dialog.querySelector('[data-df-save-edit]');
    const status = dialog.querySelector('[data-df-edit-status]');

    const updateDialogVisual = () => {
      if (image) image.src = imageForColour(colourSelect.value);
      if (thumb) thumb.classList.toggle('compact', sizeKey(sizeSelect.value) === 'compact');
    };

    close?.addEventListener('click', () => dialog.close());
    colourSelect?.addEventListener('change', updateDialogVisual);
    sizeSelect?.addEventListener('change', updateDialogVisual);

    document.querySelectorAll('[data-df-edit]').forEach((button) => {
      button.addEventListener('click', () => {
        editingKey = button.dataset.lineKey;
        const initialItem = cartItems.find((entry) => entry.key === editingKey);
        editingOriginal = initialItem ? lineIdentity(initialItem) : null;
        editingPack = button.dataset.currentPack || config.optionValues?.singlePack || '1-Pack';
        const currentSizeKey = sizeKey(button.dataset.currentSize) || 'large';
        const currentColourKey = colourKey(button.dataset.currentColour) || 'charcoal';
        sizeSelect.value = configuredSizeValue(currentSizeKey);
        colourSelect.value = configuredColourValue(currentColourKey);
        status.textContent = '';
        updateDialogVisual();
        dialog.showModal();
      });
    });

    save?.addEventListener('click', async () => {
      if (editBusy) return;
      if (!editingOriginal) {
        status.textContent = 'This basket line could not be found. Refresh the page and try again.';
        return;
      }

      const variant = findVariant(sizeSelect.value, colourSelect.value, editingPack);
      if (!variant) { status.textContent = 'That size and colour combination is not available right now.'; return; }
      if (!variant.available && Number(variant.id) !== editingOriginal.variant_id) { status.textContent = 'That combination is currently sold out.'; return; }

      editBusy = true;
      save.disabled = true;
      if (close) close.disabled = true;
      if (sizeSelect) sizeSelect.disabled = true;
      if (colourSelect) colourSelect.disabled = true;
      status.textContent = 'Updating your basket…';

      let removedLine = null;
      try {
        // Always refresh first. Shopify line keys can change when automatic discounts recalculate.
        const freshCart = await getCart();
        const liveLine = resolveLiveLine(freshCart, editingOriginal);
        if (!liveLine) throw new Error('This basket line changed while you were editing it. Please refresh and try again.');

        if (Number(variant.id) === Number(liveLine.variant_id)) {
          dialog.close();
          return;
        }

        // Remove the exact live line using its freshly resolved key BEFORE adding the replacement.
        // This avoids reusing a stale key after the bundle discount tier changes.
        removedLine = { ...liveLine, properties: cloneProperties(liveLine.properties) };
        await postJSON(config.routes.change, { id: String(liveLine.key), quantity: 0 });

        try {
          await postJSON(config.routes.add, { items: [addLinePayload(variant.id, removedLine)] });
        } catch (addError) {
          // A network error can occur after Shopify accepted the add. Re-check before rolling back.
          const afterAddError = await getCart().catch(() => null);
          const replacementIdentity = {
            ...lineIdentity(removedLine),
            key: '',
            variant_id: Number(variant.id)
          };
          const replacementExists = afterAddError ? resolveLiveLine(afterAddError, replacementIdentity) : null;
          if (!replacementExists) {
            try {
              await postJSON(config.routes.add, { items: [addLinePayload(removedLine.variant_id, removedLine)] });
            } catch (rollbackError) {
              const afterRollbackError = await getCart().catch(() => null);
              const restored = afterRollbackError ? resolveLiveLine(afterRollbackError, lineIdentity(removedLine)) : null;
              if (!restored) {
                throw new Error('We could not save the change or restore the original mat. Refresh your basket before continuing.');
              }
            }
            throw new Error('We could not save that change. Your original mat has been restored.');
          }
        }

        // Final verification: the edited slot must exist exactly once with the requested variant.
        const verifiedCart = await getCart();
        const replacementIdentity = {
          ...lineIdentity(removedLine),
          key: '',
          variant_id: Number(variant.id)
        };
        const replacement = resolveLiveLine(verifiedCart, replacementIdentity);
        if (!replacement || Number(replacement.variant_id) !== Number(variant.id)) {
          throw new Error('The basket could not be verified after the edit. Please refresh before continuing.');
        }

        reloadCart();
      } catch (error) {
        status.textContent = error.message || 'We could not update your basket.';
      } finally {
        editBusy = false;
        save.disabled = false;
        if (close) close.disabled = false;
        if (sizeSelect) sizeSelect.disabled = false;
        if (colourSelect) colourSelect.disabled = false;
      }
    });
  }

  const offer = document.querySelector('[data-df-offer]');
  if (offer) {
    const sizeSelect = offer.querySelector('[data-df-offer-size]');
    const colourSelect = offer.querySelector('[data-df-offer-colour]');
    const image = offer.querySelector('[data-df-offer-image]');
    const thumb = offer.querySelector('[data-df-offer-thumb]');
    const price = offer.querySelector('[data-df-offer-price]');
    const add = offer.querySelector('[data-df-add-offer]');
    const status = offer.querySelector('[data-df-offer-status]');

    const refreshOffer = () => {
      const variant = findVariant(sizeSelect.value, colourSelect.value, config.optionValues?.singlePack);
      if (image) { image.src = imageForColour(colourSelect.value); image.alt = `DryFloor ${colourSelect.value} mat`; }
      thumb?.classList.toggle('compact', sizeKey(sizeSelect.value) === 'compact');
      if (!variant) {
        price.textContent = '';
        add.disabled = true;
        status.textContent = 'That size and colour combination is not available right now.';
        return;
      }
      const nextCount = Math.min(4, dryfloorMatCount + 1);
      const currentSetTotal = setTotal(dryfloorSinglesTotal, dryfloorMatCount);
      const nextSetTotal = setTotal(dryfloorSinglesTotal + Number(variant.price || 0), nextCount);
      const incremental = Math.max(0, nextSetTotal - currentSetTotal);
      price.textContent = `· +${money(incremental)}`;
      add.disabled = !variant.available;
      status.textContent = variant.available ? (nextCount > 1 ? `Your ${nextCount}-mat set saving is shown in the set builder. Confirm the discount at checkout.` : 'Your order total updates as soon as the mat is added.') : 'This combination is currently sold out.';
    };

    sizeSelect?.addEventListener('change', refreshOffer);
    colourSelect?.addEventListener('change', refreshOffer);
    refreshOffer();

    add?.addEventListener('click', async () => {
      const variant = findVariant(sizeSelect.value, colourSelect.value, config.optionValues?.singlePack);
      if (!variant || !variant.available) return refreshOffer();
      add.disabled = true;
      status.textContent = 'Adding your mat…';
      const bundleId = `cart-${Date.now().toString(36)}`;
      try {
        await postJSON(config.routes.add, { items: [{ id: variant.id, quantity: 1, properties: { _dryfloor_bundle_id: bundleId, _dryfloor_slot: `extra-${Date.now()}` } }] });
        reloadCart();
      } catch (error) {
        add.disabled = false;
        status.textContent = error.message;
      }
    });
  }
})();
